/* eslint-disable */
import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import ImgPoint from '../../../../../../assets/2025/arrow.gif';
import { useStore } from '../../../../store';
import type { MisconceptionCardIconRender } from './cardIcons';
import './index.less';
import './index.en.less';

export type { MisconceptionCardIconRender } from './cardIcons';
export {
  MISCONCEPTION_CARD_ICONS,
  SSO_CARD_ICONS,
  PSA_CARD_ICONS,
  PSA_RISKS_ICONS,
} from './cardIcons';

export interface IMisconceptionItem {
  title: string;
  subTitle: string;
  desc: string;
}

export interface IMisconceptionCardsProps {
  title?: string;
  subTitle?: string;
  items?: IMisconceptionItem[];
  tips?: string;
  id?: string;
  icons: MisconceptionCardIconRender[];
  onComplete?: () => void;
}

const CLICK_LABEL: Record<string, string> = {
  tc: '點擊',
  sc: '点击',
  en: 'Click',
};

const MisconceptionCards: React.FunctionComponent<IMisconceptionCardsProps> = ({
  title,
  subTitle,
  items = [],
  tips,
  id,
  icons,
  onComplete,
}) => {
  const { language } = useStore();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(0);
  const [checkedIndices, setCheckedIndices] = useState<number[]>([0]);
  const hasCompletedRef = useRef(false);
  const clickLabel = CLICK_LABEL[language] || CLICK_LABEL.tc;

  useEffect(() => {
    setCheckedIndices([0]);
    setSelectedIndex(0);
    hasCompletedRef.current = false;
  }, [items.length]);

  useEffect(() => {
    if (
      items.length > 0 &&
      items.every((_, i) => checkedIndices.includes(i)) &&
      !hasCompletedRef.current
    ) {
      hasCompletedRef.current = true;
      onComplete?.();
    }
  }, [checkedIndices, items, onComplete]);

  const guideIndex = useMemo(() => {
    const unchecked = items
      .map((_, i) => i)
      .filter((i) => !checkedIndices.includes(i));
    return unchecked.length ? Math.min(...unchecked) : null;
  }, [items, checkedIndices]);

  const handleCardClick = useCallback((index: number) => {
    setSelectedIndex(index);
    setCheckedIndices((prev) =>
      prev.includes(index) ? prev : [...prev, index]
    );
  }, []);

  const handleCardKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>, index: number) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleCardClick(index);
      }
    },
    [handleCardClick]
  );

  return (
    <div className="misconception-cards" id={id}>
      {title && <h1 className="misconception-cards-title">{title}</h1>}
      {subTitle && (
        <h1 className="misconception-cards-sub-title">{subTitle}</h1>
      )}
      {tips && <p className="misconception-cards-tips">{tips}</p>}
      <div className="misconception-cards-list">
        {items.map((item, index) => {
          const isSelected = selectedIndex === index;
          const isChecked = checkedIndices.includes(index);
          const isGuide = guideIndex === index;
          const Icon = icons[index % icons.length];
          return (
            <div
              key={index}
              className={`misconception-card-slot${
                isSelected ? ' selected' : ''
              }`}
            >
              <div
                className={`misconception-card${isSelected ? ' selected' : ''}${
                  !isChecked ? ' is-clickable' : ''
                }${isGuide ? ' is-guide' : ''}`}
                role="button"
                tabIndex={0}
                onClick={() => handleCardClick(index)}
                onKeyDown={(event) => handleCardKeyDown(event, index)}
              >
                <div className="card-content">
                  <div className="card-title">{item.title}</div>
                  <div className="card-desc">{item.desc}</div>
                </div>
                <div className="card-footer">
                  <div className="card-icon">{Icon(isSelected)}</div>
                  {!isChecked && (
                    <div
                      className={`card-click-hint${isGuide ? ' is-guide' : ''}`}
                    >
                      <span className="card-click-label">{clickLabel}</span>
                    </div>
                  )}
                </div>
                {isGuide && <img src={ImgPoint} alt="" className="img-point" />}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default React.memo(MisconceptionCards);
